import { useState } from "react";
import AdminSlotSelect from "./AdminSlotSelect";

const VENDOR_YESNO_OPTIONS = [
  { value: "", label: "— Select —" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

/* All categories under Vendor Listing — keep in sync with
   EXPLORE_VENDORS in VendorListing.jsx */
const SERVICE_CATEGORY_OPTIONS = [
  "DJs",
  "Production",
  "Content Creation",
  "Catering",
  "Event Planner",
  "Videography & Photography",
  "Wedding Decor",
  "Hair & Makeup",
  "Bride 101",
  "Groom 101",
  "Wedding Cakes",
  "Wedding Stationery",
  "PhotoBooth",
];

const VENDOR_EDIT_SECTIONS = [
  {
    title: "About you",
    fields: [
      { k: "full_name", label: "Full name", type: "text" },
      { k: "company_name", label: "Name of the company", type: "text" },
      { k: "email", label: "Primary email address", type: "email" },
      { k: "contact_number", label: "Contact number", type: "text" },
    ],
  },
  {
    title: "Service & coverage",
    fields: [
      {
        k: "service_types",
        label: "Type of wedding service offered",
        type: "categories",
      },
      {
        k: "based_in",
        label: "Where are you based? (comma-separated)",
        type: "csv",
      },
      { k: "coverage", label: "Where are you willing to cover?", type: "text" },
      {
        k: "weddings_completed",
        label: "How many weddings completed? (e.g. 100+)",
        type: "text",
      },
    ],
  },
  {
    title: "Pricing & packages",
    fields: [
      { k: "pricing_structure", label: "Pricing structure", type: "text" },
      { k: "starting_price", label: "Starting price", type: "text" },
      {
        k: "packages_addons",
        label: "Packages, add-ons, or bespoke options",
        type: "textarea",
      },
      {
        k: "public_liability_insurance",
        label: "Public liability insurance",
        type: "text",
      },
      {
        k: "short_intro",
        label: "Short introduction for couples",
        type: "textarea",
      },
    ],
  },
  {
    title: "Business registration",
    fields: [
      {
        k: "business_registration",
        label: "Are you operating as a registered business?",
        type: "text",
      },
    ],
  },
  {
    title: "Accuracy & declarations",
    fields: [
      {
        k: "accuracy_confirmed",
        label: "Confirms all information provided is accurate and up to date",
        type: "yesno",
      },
      {
        k: "responsibility_agreed",
        label:
          "Agrees they are solely responsible for services provided to couples (contracts, payments, disputes)",
        type: "yesno",
      },
      {
        k: "public_display_consent",
        label:
          "Consents to business details being displayed publicly per Privacy Policy",
        type: "yesno",
      },
      {
        k: "content_rights_confirmed",
        label:
          "Confirms ownership / permission for all images, videos and content submitted",
        type: "yesno",
      },
      {
        k: "marketing_consent",
        label:
          "Grants permission for the directory to use submitted content for marketing",
        type: "yesno",
      },
      {
        k: "uk_compliance_confirmed",
        label:
          "Confirms compliance with all applicable UK laws, regulations & industry standards",
        type: "yesno",
      },
    ],
  },
  {
    title: "Other details & intro appointment",
    fields: [
      {
        k: "other_details",
        label: "Other details (optional)",
        type: "textarea",
      },
      {
        k: "intro_slot",
        label: "Introduction appointment",
        type: "slot",
      },
    ],
  },
];

const EDITABLE_VENDOR_FIELDS = VENDOR_EDIT_SECTIONS.flatMap((s) => s.fields);

const VendorEditForm = ({ app, onSave, onCancel }) => {
  const [form, setForm] = useState(() => {
    const initial = {};
    EDITABLE_VENDOR_FIELDS.forEach((f) => {
      if (f.type === "slot") {
        initial.intro_slot_date = app.intro_slot_date || "";
        initial.intro_slot_time = app.intro_slot_time || "";
        return;
      }
      if (f.type === "categories") {
        const raw = app[f.k];
        initial[f.k] = Array.isArray(raw) ? raw : raw ? [raw] : [];
        return;
      }
      const raw = app[f.k];
      initial[f.k] =
        f.type === "csv"
          ? Array.isArray(raw)
            ? raw.join(", ")
            : raw || ""
          : raw || "";
    });
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {};
      EDITABLE_VENDOR_FIELDS.forEach((f) => {
        if (f.type === "slot") {
          patch.intro_slot_date = form.intro_slot_date || "";
          patch.intro_slot_time = form.intro_slot_time || "";
          return;
        }
        const v = form[f.k];
        patch[f.k] =
          f.type === "csv"
            ? v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : v;
      });
      await onSave(patch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="vendor-edit-form"
      onSubmit={submit}
      className="mt-6 space-y-8"
    >
      {VENDOR_EDIT_SECTIONS.map((section) => (
        <section key={section.title} className="space-y-3">
          <h4 className="font-accent text-[11px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
            {section.title}
          </h4>
          <div className="space-y-3">
            {section.fields.map((f) => (
              <label key={f.k} className="block">
                <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                  {f.label}
                </span>
                {f.type === "textarea" ? (
                  <textarea
                    data-testid={`vendor-edit-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    rows={3}
                    className="mt-1 w-full bg-transparent border border-[var(--ld-ink)]/25 px-3 py-2 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                  />
                ) : f.type === "slot" ? (
                  <div className="mt-1">
                    <AdminSlotSelect
                      testId={`vendor-edit-${f.k}`}
                      date={form.intro_slot_date}
                      time={form.intro_slot_time}
                      onChange={({ date, time }) =>
                        setForm((s) => ({
                          ...s,
                          intro_slot_date: date,
                          intro_slot_time: time,
                        }))
                      }
                    />
                  </div>
                ) : f.type === "categories" ? (
                  <div className="mt-1 space-y-2">
                    <select
                      data-testid={`vendor-edit-${f.k}`}
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        setForm((s) =>
                          s[f.k].includes(val)
                            ? s
                            : { ...s, [f.k]: [...s[f.k], val] }
                        );
                      }}
                      className="w-full h-10 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                    >
                      <option value="">— Add a service category —</option>
                      {SERVICE_CATEGORY_OPTIONS.filter(
                        (c) => !form[f.k].includes(c)
                      ).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    {form[f.k].length > 0 ? (
                      <div
                        data-testid={`vendor-edit-${f.k}-chips`}
                        className="flex flex-wrap gap-2"
                      >
                        {form[f.k].map((c) => (
                          <span
                            key={c}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)]"
                          >
                            {c}
                            <button
                              type="button"
                              data-testid={`vendor-edit-${f.k}-remove-${c}`}
                              aria-label={`Remove ${c}`}
                              onClick={() =>
                                setForm((s) => ({
                                  ...s,
                                  [f.k]: s[f.k].filter((x) => x !== c),
                                }))
                              }
                              className="text-[var(--ld-gold)]/70 hover:text-[var(--ld-ivory)] transition-colors leading-none"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-[var(--ld-ink-muted)]">
                        No services selected — pick at least one from the
                        dropdown.
                      </p>
                    )}
                  </div>
                ) : f.type === "yesno" ? (
                  <select
                    data-testid={`vendor-edit-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    className="mt-1 w-full h-10 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                  >
                    {VENDOR_YESNO_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid={`vendor-edit-${f.k}`}
                    type={f.type === "email" ? "email" : "text"}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    className="mt-1 w-full h-10 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                  />
                )}
              </label>
            ))}
          </div>
        </section>
      ))}
      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          disabled={saving}
          data-testid="vendor-edit-save"
          className="h-10 px-5 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          data-testid="vendor-edit-cancel"
          onClick={onCancel}
          className="h-10 px-5 text-[12px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default VendorEditForm;
